// src/componentes/tienda/SeguimientoPedido.js
// «Mis pedidos» del comprador, sin cuenta:
//  - En este celular: los pedidos hechos aquí (clave privada en localStorage).
//    Se muestran desde la copia local; cada pedido en curso escucha su señal
//    (useSenalPedido) y solo se consulta cuando el gimnasio lo cambió. Permiten «Ya pagué».
//  - Desde otro celular: búsqueda con DNI + celular juntos (solo lectura).
// Rutas: /catalogo/:gymId/pedidos y /seguimiento/:gymId (enlaces antiguos con
// #clave: la clave se guarda en este celular y se retira de la barra).
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, MessageCircle, PackageSearch, Search, Store } from 'lucide-react';
import { operarOrden, prepararLlamadaDirecta } from '../../servicios/ordenServicio';
import { clavePedidoValida } from '../../utilidades/seguimientoPedido';
import { guardarDetallePedido, guardarPedidoLocal, leerCobroTienda, leerDetallePedido, leerPedidosLocales, quitarPedidoLocal,
  resumenPedido } from '../../utilidades/pedidosLocales';
import { useSenalPedido } from '../../hooks/useSenalPedido';
import { leerMarcaTienda } from '../../utilidades/carritoLocal';
import { aplicarTema } from '../../utilidades/temaVisual';
import { aplicarIdentidadTienda } from '../../utilidades/brandingDocumento';
import { construirUrlWhatsApp } from '../../dominio/logicaMensajes';
import { etiquetaEstado, pedidoTerminado, solesDeCentimos } from '../../dominio/logicaTiendaPublica';
import { EstadoPedidoPublico } from './tiendaPublica/EstadoPedidoPublico';
import { establecerMoneda } from '../../dominio/moneda';
import '../../estilos/tiendaPublica.css';

const fecha = ms => (Number.isFinite(ms) ? new Intl.DateTimeFormat('es-PE', { day: 'numeric', month: 'short', year: 'numeric' }).format(ms) : '');
const mensajeError = (e) => (e?.message && !/^internal$/i.test(e.message) ? e.message : 'No se pudo consultar. Revisa tu conexión e inténtalo de nuevo.');

const FilaPedido = ({ pedido, abierto, onAlternar, children }) => <li className={`tp-mis-pedido${abierto ? ' is-abierto' : ''}`}>
  <button type="button" className="tp-mis-pedido-fila" aria-expanded={abierto} onClick={onAlternar}>
    <span className="tp-mis-pedido-numero"><strong>{pedido.numeroPedido || 'Pedido'}</strong><small>{fecha(pedido.creado)}</small></span>
    <span className="tp-mis-pedido-estado">{pedido.estado ? etiquetaEstado(pedido.estado) : 'Toca para ver el estado'}</span>
    {Number.isFinite(pedido.totalCentimos) && <span className="tp-mis-pedido-total">{solesDeCentimos(pedido.totalCentimos)}</span>}
    <ChevronDown size={18} aria-hidden="true" />
  </button>
  {abierto && <div className="tp-mis-pedido-detalle">{children}</div>}
</li>;

/** Escucha la señal de un pedido en curso; no pinta nada. */
const VigiaPedido = ({ pedido, onCambio }) => {
  useSenalPedido(pedidoTerminado(pedido.estado) ? null : pedido.clave, pedido.version, (senal) => onCambio(pedido.clave, senal));
  return null;
};

const Seguimiento = ({ gymId }) => {
  // La marca se guardó al visitar la tienda en este navegador: 0 lecturas.
  const [marca] = useState(() => leerMarcaTienda(gymId));
  establecerMoneda(marca?.moneda);
  const [pedidos, setPedidos] = useState(() => {
    const fragmento = window.location.hash.slice(1);
    return clavePedidoValida(fragmento) ? guardarPedidoLocal(gymId, { clave: fragmento }) : leerPedidosLocales(gymId);
  });
  const [abierto, setAbierto] = useState(() => (clavePedidoValida(window.location.hash.slice(1)) ? window.location.hash.slice(1) : ''));
  // Detalle visto antes en este celular: se muestra sin consultar.
  const [detalles, setDetalles] = useState(() => Object.fromEntries(leerPedidosLocales(gymId)
    .map(p => [p.clave, leerDetallePedido(gymId, p.clave)]).filter(([, d]) => d)));
  const [cobro, setCobro] = useState(() => leerCobroTienda(gymId));
  const [ocupado, setOcupado] = useState('');
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState({ dni: '', telefono: '' });
  const [encontrados, setEncontrados] = useState(null);
  const [abiertoBusqueda, setAbiertoBusqueda] = useState('');
  const enCurso = useRef(false);

  useEffect(() => {
    aplicarTema({ primario: marca?.colorPrimario, secundario: marca?.colorSecundario, modo: 'claro' }, { persistir: false });
    aplicarIdentidadTienda({ gymId, nombre: marca?.nombre, logoBase64: marca?.logo, colorPrimario: marca?.colorPrimario });
  }, [marca, gymId]);
  useEffect(() => {
    // La clave privada no queda en la barra ni en el historial.
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    // Quien llega por un enlace con clave quiere ver ese pedido: una consulta si no está guardado.
    if (abierto && !detalles[abierto]) consultar(abierto);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tarea = async (id, fn) => {
    if (enCurso.current) return;
    enCurso.current = true; setOcupado(id); setError('');
    try { await fn(); } catch (e) { setError(mensajeError(e)); } finally { enCurso.current = false; setOcupado(''); }
  };

  const guardarDetalle = (clave, res) => {
    const { cobroManual, qrYape, ...detalle } = res;
    setDetalles(d => ({ ...d, [clave]: { ...detalle, claveConsulta: clave } }));
    if (cobroManual) setCobro(cobroManual);
    guardarDetallePedido(gymId, clave, res);
    setPedidos(guardarPedidoLocal(gymId, resumenPedido(clave, res)));
  };
  const consultar = (clave, accion = 'consultar_publico') => tarea(clave, async () => {
    const datos = { accion, gymId, idempotencyKey: clave };
    // Los datos de cobro ya guardados no se vuelven a pedir (ahorra la lectura del gym).
    if (accion === 'consultar_publico' && cobro) datos.cobroEnCache = true;
    guardarDetalle(clave, await operarOrden(datos));
  });
  // El gimnasio cambió un pedido: se anota en la lista y, si está abierto, se trae su detalle.
  const alCambiar = (clave, senal) => {
    setPedidos(guardarPedidoLocal(gymId, { clave, estado: senal.estado, version: senal.version }));
    if (abierto === clave) consultar(clave);
  };
  const desactualizado = (p) => !detalles[p.clave] || (Number(detalles[p.clave].version) || 0) < (Number(p.version) || 0);
  const alternar = (clave) => {
    if (abierto === clave) { setAbierto(''); return; }
    setAbierto(clave);
    const pedido = pedidos.find(p => p.clave === clave);
    if (pedido && desactualizado(pedido)) consultar(clave);
  };
  const buscar = (evento) => {
    evento.preventDefault();
    tarea('buscar', async () => {
      const res = await operarOrden({ accion: 'buscar_publico', gymId, dni: busqueda.dni.trim(), telefono: busqueda.telefono.trim() });
      setEncontrados(res); setAbiertoBusqueda(res.pedidos?.[0]?.id || '');
    });
  };

  const whatsapp = marca?.whatsapp ? construirUrlWhatsApp(marca.whatsapp, `Hola ${marca.nombre || ''}, quisiera consultar mi pedido.`, '') : '';

  return <div className="tienda-publica tp-seguimiento">
    <header className="tp-barra-marca">
      <div className="tp-contenedor tp-barra-marca-fila">
        <Link to={`/catalogo/${gymId}`} className="tp-marca">
          {marca?.logo ? <img src={marca.logo} alt="" /> : <span className="tp-marca-icono"><Store size={22} aria-hidden="true" /></span>}
          <strong>{marca?.nombre || 'Tienda del gimnasio'}</strong>
        </Link>
        <Link to={`/catalogo/${gymId}`} className="tp-volver"><ArrowLeft size={16} aria-hidden="true" /> Volver a la tienda</Link>
      </div>
    </header>
    <main className="tp-contenedor tp-seguimiento-cuerpo">
      <div className="tp-seguimiento-tarjeta">
        <div className="tp-seguimiento-encabezado">
          <span className="tp-seguimiento-icono" aria-hidden="true"><PackageSearch size={22} /></span>
          <div><h1>Mis pedidos</h1><p>Revisa el estado de lo que pediste{marca?.nombre ? ` en ${marca.nombre}` : ''}. No necesitas cuenta.</p></div>
        </div>
        {error && <p role="alert" className="tp-error">{error}</p>}

        <section aria-labelledby="mis-pedidos-locales" className="tp-mis-pedidos">
          <h2 id="mis-pedidos-locales">En este celular</h2>
          {pedidos.map(p => <VigiaPedido key={p.clave} pedido={p} onCambio={alCambiar} />)}
          {pedidos.length === 0
            ? <p className="tp-nota">Aquí aparecen los pedidos que hagas desde este celular. Si pediste desde otro, búscalo abajo con tu DNI.</p>
            : <ul className="tp-mis-pedidos-lista">{pedidos.map(p => <FilaPedido key={p.clave} pedido={p} abierto={abierto === p.clave} onAlternar={() => alternar(p.clave)}>
              {detalles[p.clave]
                ? <>
                  <EstadoPedidoPublico gymId={gymId} resultado={{ ...detalles[p.clave], ...(cobro ? { cobroManual: cobro } : {}) }}
                    metodosPago={marca?.metodosPago} ocupado={Boolean(ocupado)} nombreTienda={marca?.nombre} whatsapp={marca?.whatsapp}
                    onRecuperarPago={() => consultar(p.clave, 'mp_recuperar_checkout_publico')} onActualizado={(res) => guardarDetalle(p.clave, res)} />
                  <div className="tp-seguimiento-acciones">
                    {ocupado === p.clave && <span className="tp-nota" role="status">Actualizando…</span>}
                    <button type="button" className="tp-boton-texto" onClick={() => { setPedidos(quitarPedidoLocal(gymId, p.clave)); setAbierto(''); }}>Quitar de este celular</button>
                  </div>
                </>
                : <p className="tp-nota">{ocupado === p.clave ? 'Consultando…' : 'No se pudo cargar. Toca de nuevo el pedido para reintentar.'}</p>}
            </FilaPedido>)}</ul>}
        </section>

        <section aria-labelledby="mis-pedidos-buscar" className="tp-mis-pedidos">
          <h2 id="mis-pedidos-buscar">¿Pediste desde otro celular?</h2>
          <p className="tp-nota">Escribe el DNI y el celular que usaste en el pedido. Mostramos tus últimos pedidos (hasta 6 meses).</p>
          <form className="tp-mis-pedidos-buscar" onSubmit={buscar} onFocus={() => prepararLlamadaDirecta()} onInput={() => prepararLlamadaDirecta()}>
            <div className="tp-campos-dobles">
              <label className="tp-campo"><span>DNI</span><input required inputMode="numeric" pattern="[0-9]{8}" maxLength={8} autoComplete="off"
                value={busqueda.dni} placeholder="12345678" onChange={e => setBusqueda({ ...busqueda, dni: e.target.value.replace(/\D/g, '') })} /></label>
              <label className="tp-campo"><span>Celular</span><input required type="tel" inputMode="tel" autoComplete="tel-national" maxLength={15}
                value={busqueda.telefono} placeholder="987654321" onChange={e => setBusqueda({ ...busqueda, telefono: e.target.value })} /></label>
            </div>
            <button type="submit" className="tp-boton tp-boton--primario" disabled={Boolean(ocupado)}>
              <Search size={16} aria-hidden="true" />{ocupado === 'buscar' ? 'Buscando…' : 'Buscar mis pedidos'}</button>
          </form>
          {encontrados && (encontrados.pedidos?.length
            ? <ul className="tp-mis-pedidos-lista" aria-label="Pedidos encontrados">{encontrados.pedidos.map(p =>
              <FilaPedido key={p.id} pedido={p} abierto={abiertoBusqueda === p.id} onAlternar={() => setAbiertoBusqueda(abiertoBusqueda === p.id ? '' : p.id)}>
                <EstadoPedidoPublico gymId={gymId} metodosPago={marca?.metodosPago} ocupado={Boolean(ocupado)}
                  resultado={{ ...p, ...(encontrados.cobroManual && p.estado === 'pendiente_pago' ? { cobroManual: encontrados.cobroManual } : {}) }} />
              </FilaPedido>)}</ul>
            : <p className="tp-aviso tp-aviso--neutro" role="status"><span>No encontramos pedidos con esos datos. Revisa que sean el DNI y el celular que escribiste al pedir.</span></p>)}
        </section>

        {whatsapp && <a className="tp-boton tp-boton--whatsapp tp-boton--ancho" target="_blank" rel="noreferrer" href={whatsapp}>
          <MessageCircle size={18} aria-hidden="true" /> Escribir al gimnasio</a>}
      </div>
    </main>
  </div>;
};

export const SeguimientoPedido = () => {
  const { gymId } = useParams();
  return /^[a-z0-9]{1,100}$/.test(gymId || '') ? <Seguimiento key={gymId} gymId={gymId} /> : <p>Enlace inválido.</p>;
};
